/**
 * TURNING LOCK-SCREEN NOTIFICATIONS ON
 * -------------------------------------
 * Everything the browser side of push needs, in one place.
 *
 * HOW IT ACTUALLY WORKS, IN ORDER
 *   1. Register the service worker (public/sw.js). That is the background
 *      script that will receive notifications once the site is closed.
 *   2. Ask the person for permission. The browser shows this, not us, and it
 *      can only be asked from a real click - see askAndSubscribe below.
 *   3. Subscribe with the server's public key, which produces an address to
 *      deliver to.
 *   4. Send that address to the server.
 *
 * WHAT WILL NOT WORK, AND WHY
 *   - Anything other than HTTPS, except localhost. Browsers refuse.
 *   - An iPhone, unless the site has been added to the home screen first.
 *     Safari only allows notifications for an installed site. `pushSupport()`
 *     reports that case separately so the screen can explain it rather than
 *     showing a switch that silently does nothing.
 */

import { pushApi } from './api';

/**
 * What this browser can actually do. Returned as a reason rather than a
 * boolean, because "your phone cannot" and "add it to your home screen first"
 * need completely different things said to the person.
 */
export function pushSupport() {
  if (typeof window === 'undefined') return { ok: false, reason: 'unsupported' };

  const hasApi = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

  /**
   * An iPhone OR IPAD in Safari, not yet installed to the home screen.
   *
   * This asks devicePlatform() rather than testing the user agent itself,
   * and that is the whole bug it exists to fix. It used to be
   * `/iPad|iPhone|iPod/.test(navigator.userAgent)`, which is false on an
   * iPad: iPadOS 13 and later report themselves as a Mac. So an iPad fell
   * past this line, failed the `hasApi` check below because Safari does not
   * expose PushManager until the site is installed, and was told **"this
   * browser cannot do notifications"** - which is not true. It can. It needs
   * adding to the home screen first, which is exactly the thing the message
   * did not say.
   *
   * devicePlatform() has always had the Macintosh-plus-touchscreen clause.
   * This function had a second, worse copy of the same test. One place now
   * decides.
   */
  const isIos = devicePlatform() === 'ios';

  if (isIos && !isInstalled()) return { ok: false, reason: 'ios-needs-install' };
  if (!hasApi) return { ok: false, reason: 'unsupported' };
  if (Notification.permission === 'denied') return { ok: false, reason: 'blocked' };

  return { ok: true, reason: null, granted: Notification.permission === 'granted' };
}

/**
 * Which set of instructions somebody needs.
 *
 * "Tap the share button in Safari" and "tap the three dots in Chrome" are
 * different enough that showing both, or guessing wrong, is worse than not
 * explaining at all - so this is asked before anything is drawn.
 *
 * The Macintosh clause is not a mistake: an iPad running iPadOS 13 or later
 * reports itself as a Mac, and the only reliable way to tell it from a real
 * one is that it has a touchscreen.
 */
export function devicePlatform() {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent;
  const isIos =
    /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  if (isIos) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

/** True once the site has been added to the home screen and opened from there. */
export function isInstalled() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    window.navigator.standalone === true
  );
}

/** Registers the background script. Safe to call repeatedly. */
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js');
  } catch (err) {
    console.error('[push] Service worker did not register:', err.message);
    return null;
  }
}

/** The server's public key arrives as text; the browser wants bytes. */
function keyToBytes(base64) {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const raw = window.atob(padded);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

/**
 * Something short so somebody can tell "my phone" from "my laptop".
 *
 * DELIBERATELY NOT TRANSLATED. This label is sent to the server and stored
 * on the subscription, so it is written once and never re-rendered - the
 * same reason notification bodies are left alone (section 10c). Translating
 * it would only mean a list of devices in whichever languages the person
 * happened to be reading in on the day they added each one. Three of the
 * five are product names anyway.
 */
function describeDevice() {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return 'iPhone or iPad';
  if (/Android/.test(ua)) return 'Android phone';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows PC';
  return 'A device';
}

/**
 * Asks, subscribes, and tells the server.
 *
 * MUST be called straight from a click. Browsers ignore a permission request
 * that did not come from one, and some of them count an ignored request as a
 * refusal - which is permanent and cannot be undone from the site. That is why
 * nothing here runs on page load.
 *
 * Returns { ok } or { ok: false, reason } so the caller can explain itself.
 */
export async function askAndSubscribe() {
  const support = pushSupport();
  if (!support.ok) return { ok: false, reason: support.reason };

  const { available, publicKey } = await pushApi.key();
  if (!available || !publicKey) return { ok: false, reason: 'server-off' };

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: 'refused' };

  const registration = (await navigator.serviceWorker.getRegistration()) || (await registerServiceWorker());
  if (!registration) return { ok: false, reason: 'unsupported' };

  // Wait for the worker to be running, or subscribing can fail on a first
  // visit purely because it has not finished starting.
  await navigator.serviceWorker.ready;

  const subscription = await registration.pushManager.subscribe({
    // Required to be true, and honestly so: every push must result in a
    // visible notification. A browser that catches you sending silent ones
    // takes the permission away.
    userVisibleOnly: true,
    applicationServerKey: keyToBytes(publicKey),
  });

  const raw = subscription.toJSON();
  await pushApi.subscribe({
    endpoint: raw.endpoint,
    keys: raw.keys,
    label: describeDevice(),
  });

  return { ok: true };
}

/** Turns them off for this browser, both ends. */
export async function unsubscribe() {
  try {
    const registration = await navigator.serviceWorker?.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();

    if (subscription) {
      await pushApi.unsubscribe(subscription.endpoint);
      await subscription.unsubscribe();
    } else {
      // Nothing in this browser, but there may still be a stale row on the
      // server from a previous install. Clear it anyway.
      await pushApi.unsubscribe();
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: err.message };
  }
}

/** Whether this browser is currently subscribed. */
export async function isSubscribed() {
  try {
    if (!('serviceWorker' in navigator)) return false;
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    return Boolean(subscription);
  } catch {
    return false;
  }
}
